import type { CaptionProps } from "react-day-picker";
import { format } from "date-fns";
import { ChevronIcon } from "vega-icons";

import { Button } from "@vega-core/components/shadcn/button";

import { type CalendarView } from "./types";

type CustomCaptionProps = CaptionProps & {
  displayMonth: Date;
  id?: string;
  onClick?: (date: Date) => void;
  numberOfMonths?: number;
  displayIndex?: number;
  onEnterMonthView?: (displayIndex: number, displayMonth: Date) => void;
  onEnterYearView?: (displayIndex: number, displayMonth: Date) => void;
};

export function useCalendarCaption() {
  const renderCaption = (
    props: CustomCaptionProps,
    view: CalendarView,
    setView: (view: CalendarView) => void,
  ) => {
    const {
      displayMonth,
      onClick,
      numberOfMonths = 1,
      displayIndex = 0,
      onEnterMonthView,
      onEnterYearView,
    } = props;

    const handleMonthClick = () => {
      onEnterMonthView?.(displayIndex, displayMonth);
      setView("MONTH");
    };

    const handleYearClick = () => {
      onEnterYearView?.(displayIndex, displayMonth);
      setView("YEAR");
    };

    return (
      <div className="flex items-center justify-between px-1 w-full">
        <div>
          {displayIndex === 0 && (
            <Button
              className="p-0"
              onClick={() =>
                onClick?.(
                  new Date(
                    displayMonth.getFullYear(),
                    displayMonth.getMonth() - 1,
                  ),
                )
              }
              size="sm"
              variant="ghost"
            >
              <ChevronIcon className="h-6 w-6 rotate-90" />
            </Button>
          )}
        </div>
        <div className="flex items-center">
          <Button
            className="px-1"
            onClick={handleMonthClick}
            size="sm"
            variant="ghost"
          >
            {format(displayMonth, "MMMM")}
            <ChevronIcon className="inline-block h-4 w-4 text-text/secondary" />
          </Button>
          <Button
            className="px-1"
            onClick={handleYearClick}
            size="sm"
            variant="ghost"
          >
            {format(displayMonth, "yyyy")}
            <ChevronIcon className="inline-block h-4 w-4 text-text/secondary" />
          </Button>
        </div>
        <div>
          {displayIndex === numberOfMonths - 1 && (
            <Button
              className="p-0"
              onClick={() =>
                onClick?.(
                  new Date(
                    displayMonth.getFullYear(),
                    displayMonth.getMonth() + 1,
                  ),
                )
              }
              size="sm"
              variant="ghost"
            >
              <ChevronIcon className="h-6 w-6 -rotate-90" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  return { renderCaption };
}
